import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Users, List, Calendar, Heart, Plus, CalendarX, Images, Settings, Eye, EyeOff, ArrowUp, ArrowDown, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { GoogleIcon, Spinner, Avatar } from "@/components/peeps/ui";
import { api, mediaUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { colors, themeOf, gradientCss } from "@/lib/theme";
import { longDate, todayISO, rangeBounds, inRange } from "@/lib/date";
import { EventTicket } from "@/components/peeps/EventTicket";
import { MonthCalendar } from "@/components/peeps/MonthCalendar";
import { EventFormSheet } from "@/components/peeps/EventFormSheet";
import { MemberStatusList } from "@/components/peeps/MemberStatusList";
import { HomeButton } from "@/components/peeps/HomeButton";

export default function GroupDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("list");
  const [sortOrder, setSortOrder] = useState([]); // [{key, dir:'asc'|'desc'}]
  const [cursor, setCursor] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [form, setForm] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPersonal, setShowPersonal] = useState(false);
  const [schedule, setSchedule] = useState({ blocks: [], free_members: [] });
  const [range, setRange] = useState("today");     // today | week | month | all
  const [showPassed, setShowPassed] = useState(false);
  const [showDiscover, setShowDiscover] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [majorEvents, setMajorEvents] = useState([]);
  const [partifulEvents, setPartifulEvents] = useState([]);
  const [openPartiful, setOpenPartiful] = useState(null);

  useEffect(() => {
    if (!showDiscover) return;
    const p = (n) => String(n).padStart(2, "0");
    let from, to, year;
    if (viewMode === "calendar") {
      const y = cursor.getFullYear(), m = cursor.getMonth();
      year = y;
      from = `${y}-${p(m + 1)}-01`;
      to = `${y}-${p(m + 1)}-${new Date(y, m + 1, 0).getDate()}`;
    } else {
      const now = new Date();
      year = now.getFullYear();
      from = `${year}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
      const end = new Date(now); end.setMonth(end.getMonth() + 6);
      to = `${end.getFullYear()}-${p(end.getMonth() + 1)}-${p(end.getDate())}`;
    }
    let cancel = false;
    (async () => {
      try {
        const [h, mj] = await Promise.all([api.getHolidays(year), api.getMajorEvents(from, to)]);
        if (!cancel) { setHolidays(h.holidays || []); setMajorEvents(mj.events || []); }
      } catch { /* overlay is best-effort */ }
    })();
    return () => { cancel = true; };
  }, [showDiscover, viewMode, cursor]);

  const toggleSort = (key) => setSortOrder((prev) => {
    const i = prev.findIndex((s) => s.key === key);
    if (i === -1) return [...prev, { key, dir: "asc" }];        // 1st tap → ascending
    if (prev[i].dir === "asc") {                                 // 2nd tap → descending
      const n = [...prev]; n[i] = { key, dir: "desc" }; return n;
    }
    return prev.filter((s) => s.key !== key);                    // 3rd tap → clear
  });

  useEffect(() => {
    if (viewMode !== "calendar" || !showPersonal) return;
    const start = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const end = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    api.groupSchedule(String(id), start.toISOString(), end.toISOString())
      .then(setSchedule)
      .catch(() => setSchedule({ blocks: [], free_members: [] }));
  }, [id, viewMode, showPersonal, cursor]);

  const load = useCallback(async () => {
    try {
      setData(await api.getGroup(String(id)));
    } catch (e) {
      toast(e.message || "Couldn't load group");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const group = data?.group;

  useEffect(() => {
    if (!showPersonal || !group?.id) return;
    let cancel = false;
    api.getGroupPartiful(group.id).then((r) => { if (!cancel) setPartifulEvents(r.events || []); }).catch(() => {});
    return () => { cancel = true; };
  }, [showPersonal, group?.id]);
  const events = data?.events || [];
  const th = themeOf(group?.theme);
  const meId = user?.id;
  const isAdmin = group?.my_role === "admin";
  const canBlast = true;

  const topVotes = useMemo(() => events.reduce((m, e) => Math.max(m, e.vote_count), 0), [events]);

  const sorted = useMemo(() => {
    const arr = [...events];
    const cmpDate = (a, b) => (a.date + a.time).localeCompare(b.date + b.time);       // asc = earliest first
    const cmpVotes = (a, b) => (a.vote_count || 0) - (b.vote_count || 0);              // asc = fewest first
    const order = sortOrder.length ? sortOrder : [{ key: "date", dir: "asc" }];
    arr.sort((a, b) => {
      for (const { key, dir } of order) {
        let r = key === "votes" ? cmpVotes(a, b) : cmpDate(a, b);
        if (dir === "desc") r = -r;
        if (r !== 0) return r;
      }
      return cmpDate(a, b);
    });
    return arr;
  }, [events, sortOrder]);

  const visible = useMemo(
    () => (viewMode === "calendar" && selectedDate ? sorted.filter((e) => e.date === selectedDate) : sorted),
    [sorted, viewMode, selectedDate]
  );

  // List view: apply quick-view range, then split into upcoming vs passed.
  const today = todayISO();
  const rangeFiltered = useMemo(() => {
    const b = rangeBounds(range);
    return sorted.filter((e) => inRange(e.date, b));
  }, [sorted, range]);
  const upcoming = useMemo(() => rangeFiltered.filter((e) => e.date >= today), [rangeFiltered, today]);
  const passed = useMemo(
    () => rangeFiltered.filter((e) => e.date < today).sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time)),
    [rangeFiltered, today]
  );

  const toggleVote = async (eventId) => {
    setData((prev) => ({
      ...prev,
      events: prev.events.map((e) => (e.id === eventId ? { ...e, voted: !e.voted, vote_count: e.vote_count + (e.voted ? -1 : 1) } : e)),
    }));
    try { await api.vote(eventId); } catch { load(); }
  };

  const notifyGroup = async (ev) => {
    try {
      const res = await api.blast(ev.id);
      toast(res.count > 0 ? `Notified ${res.count} member${res.count === 1 ? "" : "s"}` : "No one else to notify yet");
    } catch (e) { toast(e.message || "Couldn't notify"); }
  };

  const submitEvent = async (fields, notify) => {
    setSubmitting(true);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    try {
      if (form?.mode === "edit" && form.event) {
        await api.updateEvent(form.event.id, { ...fields, tz });
        toast(`"${fields.title}" updated`);
      } else {
        const created = await api.createEvent(String(id), { ...fields, notify, tz });
        const bits = [`"${fields.title}" added`];
        if (created.google_event_id) bits.push("synced to Google Calendar");
        if (created.texted) bits.push(`notified ${created.texted} member${created.texted === 1 ? "" : "s"}`);
        toast(bits.join(" · "));
      }
      setForm(null);
      await load();
    } catch (e) {
      toast(e.message || "Couldn't save plan");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteEvent = async () => {
    if (!form?.event) return;
    try {
      await api.deleteEvent(form.event.id);
      setForm(null);
      toast("Plan deleted");
      await load();
    } catch (e) { toast(e.message || "Couldn't delete"); }
  };

  if (loading || !group) {
    return <div className="flex justify-center items-center min-h-screen" style={{ background: colors.surface }}><Spinner /></div>;
  }

  const renderTicket = (item) => (
    <div key={item.occ_id || item.id} className="animate-fade-in">
      <EventTicket
        ev={item}
        themeColor={th.solid}
        themeOn={th.on}
        isTop={topVotes > 0 && item.vote_count === topVotes}
        canEdit={isAdmin || item.created_by === meId}
        canBlast={!!canBlast}
        onToggleVote={toggleVote}
        onBlast={notifyGroup}
        onEdit={(ev) => setForm({ mode: "edit", event: ev })}
        onOpen={(ev) => navigate(`/event/${ev.id}`)}
      />
    </div>
  );

  const RANGES = [["all", "All"], ["today", "Today"], ["week", "This week"], ["month", "This month"]];

  const cover = mediaUrl(group.cover_image_url);

  return (
    <div className="min-h-screen max-w-2xl mx-auto pb-28" style={{ background: colors.surface }}>
      {/* Header */}
      <div className="relative overflow-hidden px-3 pt-4 pb-4" style={cover ? {} : { background: gradientCss(th.grad) }}>
        {cover && <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(21,22,30,0.25), rgba(21,22,30,0.7))" }} />
        <div className="relative flex items-center">
          <button data-testid="group-back" onClick={() => navigate("/groups")} className="rounded-full p-1.5" style={{ background: "rgba(21,22,30,0.3)" }}><ChevronLeft size={22} color="#fff" /></button>
          <div className="ml-2"><HomeButton size={32} bg="rgba(21,22,30,0.3)" /></div>
          <div className="flex-1" />
          <button data-testid="group-gallery-button" onClick={() => navigate(`/group/${id}/gallery`)} className="rounded-full p-1.5 mr-2" style={{ background: "rgba(21,22,30,0.3)" }}><Images size={19} color="#fff" /></button>
          <button data-testid="group-settings-button" onClick={() => navigate(`/group/${id}/settings`)} className="rounded-full p-1.5" style={{ background: "rgba(21,22,30,0.3)" }}><Settings size={19} color="#fff" /></button>
        </div>
        <div className="relative mt-3 px-1">
          <h1 className="font-display font-bold text-2xl text-white truncate">{group.name}</h1>
          <div className="flex items-center gap-1 mt-1">
            <Users size={13} color="rgba(255,255,255,0.85)" />
            <span className="font-display font-medium text-[12.5px]" style={{ color: "rgba(255,255,255,0.85)" }}>{group.member_count} {group.member_count === 1 ? "member" : "members"}</span>
            {group.google_synced && (
              <>
                <span style={{ color: "rgba(255,255,255,0.7)" }} className="mx-0.5">·</span>
                <GoogleIcon size={12} />
                <span className="font-display font-medium text-[12.5px]" style={{ color: "rgba(255,255,255,0.85)" }}>Synced</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Availability — who's around + I'm free */}
      <div className="px-4 pt-3">
        <MemberStatusList groupId={String(id)} accent={th.solid} onColor={th.on} />
      </div>

      {/* Controls */}
      <div className="flex items-center px-4 py-3">
        <div className="flex rounded-full p-[3px]" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
          <button data-testid="view-list" onClick={() => setViewMode("list")} className="flex items-center gap-1.5 py-1.5 px-3.5 rounded-full" style={{ background: viewMode === "list" ? colors.brand : "transparent" }}>
            <List size={14} color={viewMode === "list" ? colors.onBrand : colors.muted} />
            <span className="font-display font-medium text-[12.5px]" style={{ color: viewMode === "list" ? colors.onBrand : colors.muted }}>List</span>
          </button>
          <button data-testid="view-calendar" onClick={() => setViewMode("calendar")} className="flex items-center gap-1.5 py-1.5 px-3.5 rounded-full" style={{ background: viewMode === "calendar" ? colors.brand : "transparent" }}>
            <Calendar size={14} color={viewMode === "calendar" ? colors.onBrand : colors.muted} />
            <span className="font-display font-medium text-[12.5px]" style={{ color: viewMode === "calendar" ? colors.onBrand : colors.muted }}>Calendar</span>
          </button>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap justify-end">
          <button
            data-testid="toggle-holidays-events"
            onClick={() => setShowDiscover((v) => !v)}
            className="flex items-center gap-1.5 rounded-full py-1.5 px-3"
            style={{ background: showDiscover ? "#E5679E22" : colors.surfaceSecondary, border: `1px solid ${showDiscover ? "#E5679E" : colors.border}` }}
          >
            <CalendarClock size={13} color={showDiscover ? "#E5679E" : colors.onSurfaceTertiary} />
            <span className="font-display font-medium text-[12px]" style={{ color: showDiscover ? "#E5679E" : colors.onSurfaceTertiary }}>Holidays & events</span>
          </button>
          {viewMode === "calendar" && (
            <button
              data-testid="toggle-personal-schedules"
              onClick={() => setShowPersonal((v) => !v)}
              className="flex items-center gap-1.5 rounded-full py-1.5 px-3"
              style={{ background: showPersonal ? th.solid + "22" : colors.surfaceSecondary, border: `1px solid ${showPersonal ? th.solid : colors.border}` }}
            >
              {showPersonal ? <Eye size={13} color={th.solid} /> : <EyeOff size={13} color={colors.onSurfaceTertiary} />}
              <span className="font-display font-medium text-[12px]" style={{ color: showPersonal ? th.solid : colors.onSurfaceTertiary }}>
                {showPersonal ? "Personal schedules" : "Just plans"}
              </span>
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-2 px-4 pb-3">
        {["date", "votes"].map((key) => {
          const s = sortOrder.find((x) => x.key === key);
          const active = !!s;
          const rank = sortOrder.findIndex((x) => x.key === key);
          const Arrow = s && s.dir === "desc" ? ArrowDown : ArrowUp;
          return (
            <button key={key} data-testid={`sort-${key}`} onClick={() => toggleSort(key)} className="flex items-center gap-1.5 rounded-full py-2 px-3.5"
              style={{ background: active ? th.solid + "22" : colors.surfaceSecondary, border: `1px solid ${active ? th.solid : colors.border}` }}>
              {key === "votes" ? <Heart size={13} color={active ? th.solid : colors.onSurfaceTertiary} /> : <Calendar size={13} color={active ? th.solid : colors.onSurfaceTertiary} />}
              <span className="font-display font-medium text-[12.5px]" style={{ color: active ? th.solid : colors.onSurfaceTertiary }}>{key === "votes" ? "Most voted" : "By date"}</span>
              {active && <Arrow size={13} color={th.solid} data-testid={`sort-${key}-dir`} />}
              {active && sortOrder.length > 1 && (
                <span className="w-4 h-4 rounded-full flex items-center justify-center" style={{ background: th.solid }}>
                  <span className="font-mono2 font-bold text-[10px]" style={{ color: th.on }}>{rank + 1}</span>
                </span>
              )}
            </button>
          );
        })}
      </div>

      {viewMode === "list" && (
        <div className="flex gap-2 px-4 pb-3 overflow-x-auto no-scrollbar">
          {RANGES.map(([key, label]) => {
            const active = range === key;
            return (
              <button key={key} data-testid={`range-${key}`} onClick={() => setRange(key)}
                className="flex items-center rounded-full py-1.5 px-3.5 shrink-0 transition-all"
                style={{ background: active ? th.solid : colors.surfaceSecondary, border: `1px solid ${active ? th.solid : colors.border}` }}>
                <span className="font-display font-medium text-[12.5px]" style={{ color: active ? th.on : colors.onSurfaceTertiary }}>{label}</span>
              </button>
            );
          })}
        </div>
      )}
      <div className="px-4 flex flex-col gap-3">
        {viewMode === "list" && showDiscover && (() => {
          const b = rangeBounds(range);
          const seen = new Set();
          const rows = [
            ...holidays.map((h) => ({ date: (h.date || "").slice(0, 10), label: h.name, kind: "holiday" })),
            ...majorEvents.map((m) => ({ date: (m.date || "").slice(0, 10), label: `${m.title}${m.venue ? ` · ${m.venue}` : ""}`, kind: "major" })),
          ].filter((r) => r.date >= today && inRange(r.date, b))
           .sort((a, z) => a.date.localeCompare(z.date))
           .filter((r) => { const k = `${r.date}|${r.label}`; if (seen.has(k)) return false; seen.add(k); return true; });
          const rangeLabel = { today: "today", week: "this week", month: "this month", all: "upcoming" }[range] || "upcoming";
          if (!rows.length) {
            return (
              <div data-testid="list-overlay-section" className="rounded-2xl p-3" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
                <p className="font-display text-[12.5px]" style={{ color: colors.onSurfaceTertiary }}>No holidays or major events {rangeLabel}.</p>
              </div>
            );
          }
          return (
            <div data-testid="list-overlay-section" className="rounded-2xl p-3" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
              <p className="font-display font-medium text-[11px] uppercase tracking-wide mb-2 ml-0.5" style={{ color: colors.muted }}>Holidays & major events · {rangeLabel}</p>
              <div className="flex flex-col gap-1.5">
                {rows.slice(0, 40).map((r, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span style={{ width: 6, height: 6, borderRadius: 3, background: r.kind === "holiday" ? "#E0A82E" : "#E5679E" }} />
                    <span className="font-display text-[12px] w-20 shrink-0" style={{ color: colors.faint }}>{longDate(r.date)}</span>
                    <span className="font-display text-[12.5px] flex-1 truncate" style={{ color: colors.onSurfaceSecondary }}>{r.label}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
        {viewMode === "calendar" ? (
          <>
            <MonthCalendar cursor={cursor} setCursor={setCursor} events={events} overlay={showPersonal ? schedule.blocks : []} freeMembers={schedule.free_members} selectedDate={selectedDate} setSelectedDate={setSelectedDate} themeColor={th.solid} themeOn={th.on} holidays={showDiscover ? holidays : []} majorEvents={showDiscover ? majorEvents : []} />
            {showPersonal && partifulEvents.length > 0 && (
              <div data-testid="partiful-going-section" className="rounded-2xl p-3" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
                <div className="flex items-center gap-2 mb-2">
                  <span style={{ width: 6, height: 6, borderRadius: 3, background: "#B06CF0" }} />
                  <p className="font-display font-medium text-[11px] uppercase tracking-wide" style={{ color: colors.muted }}>Partiful — who's going</p>
                </div>
                <div className="flex flex-col gap-2">
                  {partifulEvents.filter((e) => (e.date || "") >= today).slice(0, 20).map((e) => (
                    <div key={e.uid}>
                      <div className="flex items-center gap-2">
                        <span className="font-display text-[12px] w-16 shrink-0" style={{ color: colors.faint }}>{e.date ? longDate(e.date) : ""}</span>
                        <span className="font-display text-[13px] flex-1 truncate" style={{ color: colors.onSurface }}>{e.title}</span>
                      </div>
                      <button data-testid={`partiful-going-${e.uid}`} onClick={() => setOpenPartiful(openPartiful === e.uid ? null : e.uid)}
                        className="ml-16 mt-1 flex items-center gap-2">
                        <div className="flex items-center">
                          {e.attendees.slice(0, 5).map((a, ai) => (
                            <div key={a.id} style={{ marginLeft: ai === 0 ? 0 : -8, zIndex: 5 - ai, borderRadius: "50%", border: `2px solid ${colors.surfaceSecondary}` }}>
                              <Avatar name={a.name} uri={a.profile_photo_url} size={24} />
                            </div>
                          ))}
                          {e.going_count > 5 && (
                            <span className="ml-1 font-display text-[11px]" style={{ color: colors.faint }}>+{e.going_count - 5}</span>
                          )}
                        </div>
                        <span className="font-display text-[11.5px] font-medium" style={{ color: "#B06CF0" }}>
                          {e.going_count} going
                        </span>
                      </button>
                      {openPartiful === e.uid && (
                        <div className="ml-16 mt-1 flex flex-wrap gap-1.5" data-testid={`partiful-attendees-${e.uid}`}>
                          {e.attendees.map((a) => (
                            <span key={a.id} className="font-display text-[11px] px-2 py-0.5 rounded-full" style={{ background: colors.surfaceTertiary, color: colors.onSurfaceSecondary }}>{a.name || "Someone"}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {selectedDate && (
              <div className="flex items-center justify-between mb-2">
                <span className="font-display font-medium text-[13px]" style={{ color: colors.onSurfaceTertiary }}>{longDate(selectedDate)}</span>
                <button data-testid="clear-date" onClick={() => setSelectedDate(null)} className="font-display font-medium text-[12.5px]" style={{ color: th.solid }}>Show all</button>
              </div>
            )}
            {visible.length === 0 ? (
              <div className="flex flex-col items-center pt-16 gap-2">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-2" style={{ background: th.solid + "22" }}>
                  <CalendarX size={28} color={th.solid} />
                </div>
                <h2 className="font-display font-bold text-lg" style={{ color: colors.onSurface }}>{selectedDate ? "Nothing this day" : "No plans yet"}</h2>
                <p className="font-display text-[13.5px]" style={{ color: colors.onSurfaceTertiary }}>Tap + to drop the first plan in.</p>
              </div>
            ) : (
              visible.map(renderTicket)
            )}
          </>
        ) : upcoming.length === 0 && passed.length === 0 ? (
          <div className="flex flex-col items-center pt-16 gap-2">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-2" style={{ background: th.solid + "22" }}>
              <CalendarX size={28} color={th.solid} />
            </div>
            <h2 className="font-display font-bold text-lg" style={{ color: colors.onSurface }}>{range === "all" ? "No plans yet" : "Nothing in this range"}</h2>
            <p className="font-display text-[13.5px]" style={{ color: colors.onSurfaceTertiary }}>{range === "all" ? "Tap + to drop the first plan in." : "Try a wider range like This month or All."}</p>
          </div>
        ) : (
          <>
            {upcoming.length > 0 ? (
              <>
                <p className="font-display font-medium text-[11.5px] uppercase tracking-wide mt-1 ml-1" style={{ color: colors.muted }} data-testid="upcoming-heading">Upcoming</p>
                {upcoming.map(renderTicket)}
              </>
            ) : (
              <p className="font-display text-[13.5px] pt-6 pb-2 text-center" style={{ color: colors.onSurfaceTertiary }}>Nothing upcoming{range === "all" ? "" : " in this range"}.</p>
            )}

            {passed.length > 0 && (
              <>
                <button
                  data-testid="toggle-passed"
                  onClick={() => setShowPassed((v) => !v)}
                  className="flex items-center justify-center gap-1.5 rounded-full py-2 px-4 mx-auto mt-2 transition-all"
                  style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}
                >
                  <span className="font-display font-medium text-[12.5px]" style={{ color: colors.onSurfaceTertiary }}>
                    {showPassed ? "Hide passed" : `Show passed (${passed.length})`}
                  </span>
                </button>
                {showPassed && (
                  <div className="flex flex-col gap-3" style={{ opacity: 0.7 }} data-testid="passed-section">
                    <p className="font-display font-medium text-[11.5px] uppercase tracking-wide mt-1 ml-1" style={{ color: colors.muted }}>Passed</p>
                    {passed.map(renderTicket)}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      <button data-testid="add-event-fab" onClick={() => setForm({ mode: "add" })} className="fixed bottom-24 z-30 w-14 h-14 rounded-full flex items-center justify-center" style={{ right: "max(1rem, calc(50vw - 20rem))", background: th.solid, boxShadow: "0 6px 20px rgba(0,0,0,0.4)" }}>
        <Plus size={26} color={th.on} />
      </button>

      {form && (
        <EventFormSheet
          visible
          mode={form.mode}
          initial={form.event}
          googleSynced={group.google_synced}
          submitting={submitting}
          onClose={() => setForm(null)}
          onSubmit={submitEvent}
          onDelete={form.mode === "edit" ? deleteEvent : undefined}
        />
      )}
    </div>
  );
}
